import React, { useRef, useState, useEffect, useCallback } from 'react';
import Card from './Card';
import '../style/admin.css';

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function Category({ title, films, onCardClick, edit }) {
  const list = films || [];
  const rowRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, list.length]);

  const scrollByPage = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    const item = el.querySelector('.bb-row__item');
    const gap = 16;
    const step = item ? item.getBoundingClientRect().width + gap : el.clientWidth;
    const perPage = Math.max(1, Math.floor((el.clientWidth + gap) / step));
    el.scrollBy({ left: dir * step * perPage, behavior: prefersReduced ? 'auto' : 'smooth' });
  };

  return (
    <section className="bb-category">
      <div className="bb-cat-head">
        <h2 className="bb-category__title bb-display">{title}</h2>
        {edit && (
          <div className="bb-cat-tools">
            <button type="button" className="bb-icon-btn" onClick={() => edit.onMoveCategory(edit.catIndex, -1)} disabled={edit.catIndex === 0} aria-label="Monter la catégorie">▲</button>
            <button type="button" className="bb-icon-btn" onClick={() => edit.onMoveCategory(edit.catIndex, 1)} disabled={edit.catIndex === edit.catCount - 1} aria-label="Descendre la catégorie">▼</button>
            <button type="button" className="bb-icon-btn" onClick={() => edit.onRenameCategory(edit.catIndex)} aria-label="Renommer">✏️</button>
            <button type="button" className="bb-icon-btn bb-icon-btn--danger" onClick={() => edit.onDeleteCategory(edit.catIndex)} aria-label="Supprimer la catégorie">🗑️</button>
            <button type="button" className="bb-btn bb-btn--sm bb-btn--primary" onClick={() => edit.onAddFilm(edit.catIndex)}>+ Ajouter</button>
          </div>
        )}
      </div>

      {list.length === 0 ? (
        <p className="bb-category__empty">Aucun élément pour l'instant.</p>
      ) : (
        <div className="bb-row-wrap">
          <button
            type="button"
            className={`bb-row-arrow bb-row-arrow--left ${canLeft ? 'is-visible' : ''}`}
            onClick={() => scrollByPage(-1)}
            aria-label="Faire défiler vers la gauche"
            aria-hidden={!canLeft}
            tabIndex={canLeft ? 0 : -1}
          >
            ‹
          </button>
          <div className="bb-row" ref={rowRef}>
            {list.map((film, index) => (
              <div className="bb-row__item" key={film.code || index}>
                <Card
                  film={film}
                  onClick={() => (edit ? edit.onEditFilm(edit.catIndex, index) : onCardClick(film))}
                />
                {edit && (
                  <div className="bb-card-tools">
                    <button type="button" className="bb-icon-btn" onClick={() => edit.onMoveFilm(edit.catIndex, index, -1)} disabled={index === 0} aria-label="Déplacer à gauche">◀</button>
                    <button type="button" className="bb-icon-btn" onClick={() => edit.onEditFilm(edit.catIndex, index)} aria-label="Modifier">✏️</button>
                    <button type="button" className="bb-icon-btn bb-icon-btn--danger" onClick={() => edit.onDeleteFilm(edit.catIndex, index)} aria-label="Supprimer">🗑️</button>
                    <button type="button" className="bb-icon-btn" onClick={() => edit.onMoveFilm(edit.catIndex, index, 1)} disabled={index === list.length - 1} aria-label="Déplacer à droite">▶</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            className={`bb-row-arrow bb-row-arrow--right ${canRight ? 'is-visible' : ''}`}
            onClick={() => scrollByPage(1)}
            aria-label="Faire défiler vers la droite"
            aria-hidden={!canRight}
            tabIndex={canRight ? 0 : -1}
          >
            ›
          </button>
        </div>
      )}
    </section>
  );
}

export default Category;
